import type { KeyboardEvent, MouseEvent } from "react";

const CONTROLS =
  "input, textarea, select, button, a, [role='button'], [role='slider'], [contenteditable='true']";

/**
 * Whether a click landed on something inside the row that already owns it — a field, a button, a link.
 *
 * The search runs from what was clicked up to the row itself and stops there. That boundary matters
 * twice over: the row may be a button in its own right, and a row nested inside another must answer for
 * its own clicks rather than deferring to the one it sits in.
 */
export const isInteractiveClickTarget = (target: EventTarget | null, row?: EventTarget | null): boolean => {
  let element = target instanceof Element ? target : null;
  while (element && element !== row) {
    if (element.matches(CONTROLS)) return true;
    element = element.parentElement;
  }
  return false;
};

/**
 * Makes a whole row open what it stands for. Every requirement list in the app uses it, so the habit
 * carries: point at a row, click anywhere that is not itself a control, and it opens.
 *
 * The row is the only way in — there is no "Open" link beside it — so it answers the keyboard too:
 * one tab stop per row, Enter or Space to open. No `role="button"`, since these rows hold buttons of
 * their own and a table row is a row; the tab stop is what a keyboard actually needs.
 *
 * The click stops here once handled, so a row folded inside another row opens itself and not its host.
 * The hover tint is left to the caller — `smBackgroundSecondary` is what the rest of the app uses.
 */
export const rowOpenProps = (onOpen: () => void) => ({
  cursor: "pointer" as const,
  tabIndex: 0,
  onClick: (e: MouseEvent) => {
    if (isInteractiveClickTarget(e.target, e.currentTarget)) return;
    e.stopPropagation();
    onOpen();
  },
  onKeyDown: (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    onOpen();
  },
});
